import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getFeaturedArticles, getLatestArticles, getAllArticles } from '@/mocks/articles';
import { getAllProducts, getLimitedProducts } from '@/mocks/products';
import { getFAQItems } from '@/mocks/faq';
import { getAllCategories } from '@/mocks/categories';
import { OrganizationLD, WebSiteLD, WebPageLD, FAQPageLD } from '@/components/feature/JsonLD';
import type { Article } from '@/mocks/articles';
import type { Product } from '@/mocks/products';

function HeroSection() {
  return (
    <section className="relative w-full min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Soft%20dreamy%20pink%20gradient%20background%20with%20floating%20sparkles%20and%20stars%2C%20kawaii%20aesthetic%2C%20ethereal%20glowing%20particles%2C%20soft%20bokeh%20effect%2C%20fantasy%20atmosphere%2C%20magical%20pastel%20color%20palette%2C%20whimsical%20environment%2C%20clean%20minimalist%20composition%2C%20dreamy%20and%20romantic%20vibe&width=1920&height=1080&seq=mengbai-hero-bg&orientation=landscape"
          alt="萌白酱"
          className="w-full h-full object-cover object-top"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/40"></div>
      <div className="relative z-10 w-full px-4 md:px-6 text-center">
        <div className="animate-fade-in">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-5 py-2 mb-6">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-verified-badge-fill text-accent-500 text-sm"></i>
            </div>
            <span className="text-sm text-white/90">知名虚拟UP主 &amp; Cosplay创作者</span>
          </div>
        </div>
        <h1 className="text-4xl md:text-6xl lg:text-7xl font-heading text-white mb-6 animate-fade-in-up animation-delay-100">
          萌白酱
        </h1>
        <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto mb-10 animate-fade-in-up animation-delay-200 leading-relaxed">
          汇聚最新动态、视频合集、周边产品及粉丝社区入口<br className="hidden sm:block" />
          带你全面了解萌白酱的创作世界与独特魅力
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up animation-delay-300">
          <Link
            to="/category/videos"
            className="w-full sm:w-auto px-8 py-3.5 bg-primary-500 text-background-50 font-medium rounded-full hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap text-sm"
          >
            <i className="ri-play-circle-line mr-2"></i>
            观看最新视频
          </Link>
          <Link
            to="/category/merch"
            className="w-full sm:w-auto px-8 py-3.5 bg-white/10 backdrop-blur-sm border border-white/30 text-white font-medium rounded-full hover:bg-white/20 transition-all duration-200 cursor-pointer whitespace-nowrap text-sm"
          >
            <i className="ri-shopping-bag-3-line mr-2"></i>
            浏览周边产品
          </Link>
        </div>
        <div className="mt-16 animate-fade-in animation-delay-500">
          <div className="w-8 h-12 mx-auto flex items-center justify-center">
            <i className="ri-mouse-line text-white/60 text-xl animate-bounce"></i>
          </div>
        </div>
      </div>
    </section>
  );
}

function FeaturedArticlesSection() {
  const featured = getFeaturedArticles();
  const [currentIndex, setCurrentIndex] = useState(0);

  const next = () => {
    setCurrentIndex((prev) => (prev + 1) % featured.length);
  };

  const prev = () => {
    setCurrentIndex((prev) => (prev - 1 + featured.length) % featured.length);
  };

  if (featured.length === 0) return null;
  const current = featured[currentIndex];

  return (
    <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-3">
            <i className="ri-fire-line text-accent-500 mr-2"></i>
            精选推荐
          </h2>
          <p className="text-sm text-foreground-500">萌白酱最受欢迎的内容，不容错过</p>
        </div>

        <div className="relative">
          <Link
            to={`/article/${current.slug}`}
            className="block group cursor-pointer"
          >
            <div className="relative rounded-2xl overflow-hidden aspect-[21/9]">
              <img
                src={current.coverImage}
                alt={current.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <span className="inline-block px-3 py-1 bg-accent-500 text-background-50 text-xs font-medium rounded-full mb-3">
                  {current.categoryName}
                </span>
                <h3 className="text-xl md:text-2xl font-heading text-white mb-2 group-hover:text-accent-300 transition-colors duration-200">
                  {current.title}
                </h3>
                <p className="text-sm text-white/70 max-w-2xl line-clamp-2">
                  {current.excerpt}
                </p>
                <div className="flex items-center gap-4 mt-3 text-white/50 text-xs">
                  <span><i className="ri-calendar-line mr-1"></i>{current.publishDate}</span>
                  <span><i className="ri-eye-line mr-1"></i>{(current.views / 10000).toFixed(1)}万</span>
                  <span><i className="ri-heart-line mr-1"></i>{(current.likes / 10000).toFixed(1)}万</span>
                </div>
              </div>
            </div>
          </Link>

          <button
            onClick={prev}
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm text-white hover:bg-white/40 transition-all duration-200 cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>
          <button
            onClick={next}
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm text-white hover:bg-white/40 transition-all duration-200 cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>
        </div>

        <div className="flex items-center justify-center gap-2 mt-5">
          {featured.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
                index === currentIndex ? 'w-6 bg-accent-500' : 'bg-foreground-300'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function CategoryGrid() {
  const allCategories = getAllCategories();

  return (
    <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-100">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-3">
            <i className="ri-grid-line text-secondary-500 mr-2"></i>
            内容分类
          </h2>
          <p className="text-sm text-foreground-500">探索萌白酱的精彩内容世界</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {allCategories.map((cat, index) => (
            <Link
              key={cat.id}
              to={`/category/${cat.slug}`}
              className="group relative overflow-hidden rounded-2xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img
                  src={cat.coverImage}
                  alt={cat.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className={`${cat.icon} text-lg`}></i>
                  </div>
                  <h3 className="text-lg font-heading text-foreground-900">{cat.name}</h3>
                </div>
                <p className="text-sm text-foreground-500 leading-relaxed mb-3">{cat.description}</p>
                <span className="text-xs text-foreground-400">
                  <i className="ri-file-text-line mr-1"></i>
                  {cat.articleCount} 篇内容
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function LatestArticles() {
  const latestArticles = getLatestArticles(6);

  return (
    <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-50">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <div>
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-2">
              <i className="ri-article-line text-primary-500 mr-2"></i>
              最新动态
            </h2>
            <p className="text-sm text-foreground-500">萌白酱的最新内容更新</p>
          </div>
          <Link
            to="/category/news"
            className="hidden sm:inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            查看全部 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div data-product-shop className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {latestArticles.map((item: Article) => (
            <Link
              key={item.id}
              to={`/article/${item.slug}`}
              className="group bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img
                  src={item.coverImage}
                  alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="px-2 py-0.5 bg-secondary-100 text-secondary-800 text-xs font-medium rounded-full">
                    {item.categoryName}
                  </span>
                  {item.isFeatured && (
                    <span className="px-2 py-0.5 bg-accent-100 text-accent-800 text-xs font-medium rounded-full">
                      推荐
                    </span>
                  )}
                </div>
                <h3 className="text-base font-heading text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
                  {item.title}
                </h3>
                <p className="text-sm text-foreground-500 line-clamp-2 mb-3">
                  {item.excerpt}
                </p>
                <div className="flex items-center justify-between text-xs text-foreground-400">
                  <span>{item.publishDate}</span>
                  <span><i className="ri-eye-line mr-1"></i>{(item.views / 10000).toFixed(1)}万</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
        <div className="mt-8 text-center sm:hidden">
          <Link
            to="/category/news"
            className="inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors duration-200 cursor-pointer"
          >
            查看全部动态 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}

function ProductsShowcase() {
  const allProducts = getAllProducts();
  const limitedProducts = getLimitedProducts();

  return (
    <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-100">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <div>
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-2">
              <i className="ri-shopping-bag-3-line text-accent-500 mr-2"></i>
              周边产品
            </h2>
            <p className="text-sm text-foreground-500">萌白酱官方正版周边，限量发售中</p>
          </div>
          <Link
            to="/category/merch"
            className="hidden sm:inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            全部周边 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {limitedProducts.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-fire-fill text-accent-500"></i>
              </div>
              <span className="text-sm font-medium text-accent-700">限量发售</span>
            </div>
            <div data-product-shop className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {limitedProducts.map((prod: Product) => (
                <Link
                  key={prod.id}
                  to="/category/merch"
                  className="group bg-background-50 border border-accent-200/60 rounded-2xl overflow-hidden hover:border-accent-300 transition-all duration-300 cursor-pointer"
                >
                  <div className="aspect-square overflow-hidden relative">
                    <img
                      src={prod.image}
                      alt={prod.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <span className="absolute top-3 left-3 px-2.5 py-1 bg-accent-500 text-background-50 text-xs font-medium rounded-full">
                      限定
                    </span>
                    {prod.isNew && (
                      <span className="absolute top-3 right-3 px-2.5 py-1 bg-primary-500 text-background-50 text-xs font-medium rounded-full">
                        新品
                      </span>
                    )}
                  </div>
                  <div className="p-4">
                    <span className="text-xs text-foreground-400 bg-background-100 px-2 py-0.5 rounded-full">{prod.category}</span>
                    <h3 className="text-sm font-heading text-foreground-900 mt-2 mb-1 line-clamp-1">{prod.name}</h3>
                    <p className="text-xs text-foreground-500 line-clamp-2 mb-3">{prod.description}</p>
                    <div className="flex items-center gap-2">
                      <span className="text-base font-bold text-primary-600">¥{prod.price}</span>
                      {prod.originalPrice && (
                        <span className="text-xs text-foreground-400 line-through">¥{prod.originalPrice}</span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {allProducts.length > limitedProducts.length && (
          <div data-product-shop className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {allProducts.filter(p => !p.isLimited).map((prod: Product) => (
              <Link
                key={prod.id}
                to="/category/merch"
                className="group bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
              >
                <div className="aspect-square overflow-hidden">
                  <img
                    src={prod.image}
                    alt={prod.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-3">
                  <span className="text-xs text-foreground-400 bg-background-100 px-2 py-0.5 rounded-full">{prod.category}</span>
                  <h3 className="text-sm font-heading text-foreground-900 mt-1.5 line-clamp-1">{prod.name}</h3>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <span className="text-sm font-bold text-primary-600">¥{prod.price}</span>
                    {prod.originalPrice && (
                      <span className="text-xs text-foreground-400 line-through">¥{prod.originalPrice}</span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        <div className="mt-8 text-center sm:hidden">
          <Link
            to="/category/merch"
            className="inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors duration-200 cursor-pointer"
          >
            查看全部周边 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}

function CommunitySection() {
  return (
    <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-3">
            <i className="ri-group-line text-secondary-500 mr-2"></i>
            加入粉丝社区
          </h2>
          <p className="text-sm text-foreground-500 max-w-xl mx-auto">
            和千万粉丝一起，关注萌白酱的最新动态，参与精彩活动
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { icon: 'ri-bilibili-line', name: 'B站频道', desc: '关注萌白酱Official，第一时间观看视频和直播', color: 'bg-secondary-100 text-secondary-700', action: '前往B站' },
            { icon: 'ri-weibo-line', name: '微博', desc: '获取萌白酱日常动态和精彩瞬间分享', color: 'bg-primary-100 text-primary-700', action: '前往微博' },
            { icon: 'ri-discord-line', name: '粉丝社群', desc: '加入粉丝交流群，与同好一起互动讨论', color: 'bg-accent-100 text-accent-700', action: '加入社群' },
            { icon: 'ri-qq-line', name: 'QQ粉丝群', desc: '萌白酱官方QQ群，活动通知和粉丝福利', color: 'bg-secondary-100 text-secondary-700', action: '加入QQ群' },
          ].map((item, index) => (
            <a
              key={index}
              href="#"
              className="group bg-background-50 border border-background-200/70 rounded-2xl p-6 text-center hover:border-primary-200 transition-all duration-300 cursor-pointer"
            >
              <div className={`w-14 h-14 md:w-16 md:h-16 mx-auto flex items-center justify-center rounded-2xl ${item.color} mb-4 group-hover:scale-110 transition-transform duration-300`}>
                <i className={`${item.icon} text-2xl`}></i>
              </div>
              <h3 className="text-base font-heading text-foreground-900 mb-2">{item.name}</h3>
              <p className="text-sm text-foreground-500 mb-4">{item.desc}</p>
              <span className="text-sm text-primary-500 font-medium group-hover:text-primary-600 transition-colors duration-200">
                {item.action} <i className="ri-arrow-right-up-line text-xs"></i>
              </span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const faqs = getFAQItems();
  const [openId, setOpenId] = useState<string | null>(null);

  const toggle = (id: string) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-100">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-3">
            <i className="ri-question-answer-line text-primary-500 mr-2"></i>
            常见问题
          </h2>
          <p className="text-sm text-foreground-500">关于萌白酱，你可能想了解的问题</p>
        </div>
        <div className="space-y-3">
          {faqs.map((faq) => (
            <div
              key={faq.id}
              className="bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden"
            >
              <button
                onClick={() => toggle(faq.id)}
                className="w-full flex items-center justify-between p-5 text-left cursor-pointer hover:bg-background-100/50 transition-colors duration-200"
              >
                <span className="text-sm font-medium text-foreground-900 pr-4">{faq.question}</span>
                <div className={`w-6 h-6 flex items-center justify-center rounded-full transition-all duration-300 flex-shrink-0 ${
                  openId === faq.id ? 'bg-primary-500 text-background-50 rotate-180' : 'bg-background-200 text-foreground-500'
                }`}>
                  <i className="ri-arrow-down-s-line text-sm"></i>
                </div>
              </button>
              {openId === faq.id && (
                <div className="px-5 pb-5 animate-fade-in">
                  <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="w-full px-4 md:px-6 py-20 md:py-28 relative overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Soft%20dreamy%20gradient%20background%20with%20floating%20heart%20shapes%20and%20sparkles%2C%20kawaii%20romantic%20atmosphere%2C%20pastel%20pink%20and%20coral%20tones%2C%20ethereal%20glowing%20particles%2C%20whimsical%20fantasy%20environment%2C%20clean%20composition%2C%20magical%20vibe&width=1920&height=600&seq=mengbai-cta-bg&orientation=landscape"
          alt=""
          className="w-full h-full object-cover object-top"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-r from-primary-500/80 to-secondary-500/70"></div>
      <div className="relative z-10 max-w-2xl mx-auto text-center">
        <h2 className="text-2xl md:text-4xl font-heading text-white mb-4">
          加入萌白酱的创作世界
        </h2>
        <p className="text-sm md:text-base text-white/80 mb-8">
          关注萌白酱，第一时间获取最新视频、cosplay写真和活动资讯
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/category/videos"
            className="w-full sm:w-auto px-8 py-3.5 bg-white text-primary-600 font-medium rounded-full hover:bg-white/90 transition-all duration-200 cursor-pointer whitespace-nowrap text-sm"
          >
            <i className="ri-play-circle-fill mr-2"></i>
            开始探索
          </Link>
          <Link
            to="/about"
            className="w-full sm:w-auto px-8 py-3.5 bg-white/10 backdrop-blur-sm border border-white/30 text-white font-medium rounded-full hover:bg-white/20 transition-all duration-200 cursor-pointer whitespace-nowrap text-sm"
          >
            <i className="ri-information-line mr-2"></i>
            了解更多
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const faqs = getFAQItems();

  useEffect(() => {
    document.title = '萌白酱官方粉丝落地页｜最新动态与周边合集';
  }, []);

  const siteUrl = 'https://mengbaijiang.com';
  const siteName = '萌白酱Official';
  const siteDesc = '萌白酱是深受粉丝喜爱的知名虚拟UP主与cosplay创作者，本页汇聚萌白酱最新动态、视频合集、周边产品及粉丝社区入口，带你全面了解萌白酱的创作世界与独特魅力。';

  return (
    <>
      <OrganizationLD
        name={siteName}
        url={siteUrl}
        logo={`${siteUrl}/logo.png`}
        description={siteDesc}
        sameAs={[
          'https://space.bilibili.com/萌白酱',
          'https://weibo.com/萌白酱',
          'https://www.douyin.com/user/萌白酱',
        ]}
      />
      <WebSiteLD url={siteUrl} name={siteName} description={siteDesc} />
      <WebPageLD
        url={siteUrl}
        name="萌白酱官方粉丝落地页"
        description={siteDesc}
        breadcrumb={[{ name: '首页', url: siteUrl }]}
      />
      <FAQPageLD questions={faqs.map(f => ({ question: f.question, answer: f.answer }))} />

      <main>
        <HeroSection />
        <FeaturedArticlesSection />
        <CategoryGrid />
        <LatestArticles />
        <ProductsShowcase />
        <CommunitySection />
        <FAQSection />
        <CTASection />
      </main>
    </>
  );
}